import type { DocumentRecord } from '@/types/catalog'
import { pnidPageEntriesFrom } from '@/utils/resolveGraphOpen'

/**
 * Resolve the board-usable reference — `(document_id, doc_type)` — for a catalog
 * {@link DocumentRecord} that is being ADDED to a board.
 *
 * A processed P&ID/PFD is added by its parsed graph (doc_type `'graph'`) so the
 * board renders the diagram via the canonical graph thumbnail path — the same
 * convention DocumentsModal (in-board "Add files") and the backend master-board
 * sync already use. Everything else (an unprocessed P&ID with no graph yet, a
 * plain document, datasheet, timeseries, …) is added by its own id and catalog
 * type.
 *
 * FS-556: the Data Catalog "Open in new board" action used to add P&IDs by
 * their source id + raw `doc.type`, diverging from every other entry point;
 * that made a P&ID card persist/resolve as a generic 'document' and skip the
 * diagram render. Routing every catalog→board add through this helper removes
 * the divergence.
 *
 * MULTI-PAGE P&IDs resolve to their FIRST parsed page. Their pages live in the
 * `pnid_graph_ids` page map and they have no scalar `graph_id` at all, so
 * reading only `graph_id` dropped a 3-page drawing onto a board as a
 * `doc_type='pnid'` card — the page image rendered but no detections did,
 * because the card pointed at the PDF rather than at any of the page graphs.
 *
 * The page map is NOT a readiness signal: the fan-out pre-generates an id for
 * every page BEFORE any of them is parsed (`async-jobs-bridge.service.ts`), so
 * a page's graph document may not exist yet. Selecting the first page whose id
 * is merely present would persist a board card pointing at nothing — worse than
 * the old behaviour, which at least rendered the raw page. So pages are filtered
 * through `pnid_workflows.completed_pages` and a drawing with none finished yet
 * falls back to its source document, exactly as before.
 *
 * This is the SELECTION-side counterpart to {@link resolveBoardDocType}, which
 * is the RENDER-side resolver (stored position → visual type). Pure function so
 * it is unit-testable in isolation.
 */
export function resolveBoardDocRef(
  doc: DocumentRecord,
): { document_id: string; doc_type: DocumentRecord['type'] } {
  if (doc.type === 'pnid' || doc.type === 'pfd') {
    const entries = pnidPageEntriesFrom(doc.properties)
    if (entries.length > 0) {
      // `undefined` predates per-page workflow tracking — trust the map then.
      // An empty array means the fan-out has run but nothing has landed yet.
      const completed = doc.properties.pnid_workflows?.completed_pages
      const ready =
        completed === undefined ? entries : entries.filter((e) => completed.includes(e.page))
      if (ready.length > 0) {
        return { document_id: ready[0].graphId, doc_type: 'graph' }
      }
      return { document_id: doc.id, doc_type: doc.type }
    }
    if (doc.properties.graph_id) {
      return { document_id: doc.properties.graph_id, doc_type: 'graph' }
    }
  }
  return { document_id: doc.id, doc_type: doc.type }
}

/**
 * Every id a board may already hold `doc` under: its resolved board id, and —
 * when those differ — its raw doc id, which is how a multi-page P&ID was
 * persisted before it resolved to a page graph.
 *
 * Selection state must treat them as one unit. Removing only the resolved id
 * leaves a legacy card selected and un-removable; adding only the resolved id
 * while the legacy one is still in the set saves BOTH, putting two cards on the
 * board for one drawing.
 */
export function boardIdsFor(doc: DocumentRecord): string[] {
  const boardId = resolveBoardDocRef(doc).document_id
  return boardId === doc.id ? [boardId] : [boardId, doc.id]
}

/**
 * Add `doc`'s resolved board id to `set`, dropping any legacy id for the same
 * doc so the set never holds two ids for one drawing.
 */
export function selectBoardId(set: Set<string>, doc: DocumentRecord): void {
  const boardId = resolveBoardDocRef(doc).document_id
  for (const id of boardIdsFor(doc)) {
    if (id !== boardId) set.delete(id)
  }
  set.add(boardId)
}
